import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = article ? getRelatedArticles(article) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${article.title} - 91.con 导航`;
  const canonicalUrl = `https://bosomfriendcat.cn/article/${article.slug}`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={article.excerpt}
        keywords={`91.con,${article.tags.join(",")},${article.categoryName}`}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.date}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: pageTitle,
          description: article.excerpt,
          url: canonicalUrl,
        }}
        scriptId="schema-webpage"
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: {
            "@type": "Person",
            name: article.author,
          },
          image: article.imageUrl,
          publisher: {
            "@type": "Organization",
            name: "91.con 导航",
          },
        }}
        scriptId="schema-article"
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://bosomfriendcat.cn/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: article.categoryName,
              item: `https://bosomfriendcat.cn/category/${article.categorySlug}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
            },
          ],
        }}
        scriptId="schema-breadcrumb"
      />

      <Navbar />

      <main className="min-h-screen pt-20 bg-background-50">
        <article>
          {/* Header */}
          <div className="bg-background-100 py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: "首页", href: "/" },
                    {
                      label: article.categoryName,
                      href: `/category/${article.categorySlug}`,
                    },
                    { label: article.title },
                  ]}
                />
              </div>
              <div className="mt-6">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap hover:bg-accent-200 transition-colors"
                >
                  <i className="ri-folder-line"></i>
                  {article.categoryName}
                </Link>
              </div>
              <h1 className="mt-4 text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <div className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  <span>{article.author}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <div className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  <span>{article.date}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <div className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  <span>阅读约需 {article.readTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-6">
            <div className="aspect-[16/9] rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>
          </div>

          {/* Content */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-950 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:mb-4 [&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5
                [&_ol]:mb-4 [&_ol]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-5
                [&_li]:text-foreground-700
                [&_strong]:text-foreground-950 [&_strong]:font-semibold
                [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-primary-700
                [&_blockquote]:border-l-4 [&_blockquote]:border-primary-300 [&_blockquote]:bg-background-100 [&_blockquote]:px-4 [&_blockquote]:py-3 [&_blockquote]:rounded-r-lg [&_blockquote]:my-4
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 font-medium">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={related.title}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-400">{related.date}</span>
                        <span className="text-xs text-foreground-300">·</span>
                        <span className="text-xs text-foreground-400">{related.readTime}</span>
                      </div>
                      <h3 className="font-semibold text-foreground-950 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}